import { useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { IInstrumentTemplateSummary } from '@brushfire/types'
import { mapInstrumentDraftToBuilderQuestions } from '@/features/instruments/question-builder/mapInstrumentDraftToBuilder'
import { useInstrumentTemplateDetail } from '@/features/instruments/templates/api/template-queries'
import { usePlatformInstrumentTemplateDetail } from '@/features/platform/api/platform-instrument-templates-queries'
import { formatTemplateEstCompletionLabel } from '@/features/instruments/templates/lib/formatTemplateEstCompletion'
import { libraryModalCloseButtonSx } from '@/features/library/lib/libraryModalCloseButtonSx'
import { getTemplateCategoryLabel } from '@/features/instruments/templates/lib/templateCategoryLabels'
import { mapTemplateSeedToInstrumentDraft } from '@/features/instruments/templates/lib/mapTemplateSeedToInstrumentDraft'
import { TemplatePreviewQuestionsSkeleton } from './skeletons'
import { TemplateIcon } from './TemplateIcon'
import { TemplatePreviewQuestionCard } from './TemplatePreviewQuestionCard'

const HEADER_BORDER = '#f0f4f8'
const TITLE = '#0d1b2a'
const DESC_BLUE = '#3b82f6'
const FOOTER_BORDER = '#e4e9f0'

type Props = Readonly<{
  open: boolean
  template: IInstrumentTemplateSummary | null
  /** Prefer API category label from list response; falls back to local map. */
  categoryLabel?: string
  onClose: () => void
  onUseTemplate?: (template: IInstrumentTemplateSummary) => void
  /** Browse-only preview — hides "Use this template" (nav Template Library). */
  previewOnly?: boolean
  /** Platform Admin — show "Used X times" in header meta row (BFP-579). */
  showUsageCount?: boolean
  /**
   * Which detail API to call.
   * Platform Admin must use `platformAdmin` — Sensemaker `/instrument-templates/:id` returns 403.
   */
  detailSource?: 'sensemaker' | 'platformAdmin'
}>

export function TemplatePreviewModal({
  open,
  template,
  categoryLabel: categoryLabelProp,
  onClose,
  onUseTemplate,
  previewOnly = false,
  showUsageCount = false,
  detailSource = 'sensemaker',
}: Props) {
  const templateId = template?.id ?? null
  const enabled = open && Boolean(templateId)
  const usePlatform = detailSource === 'platformAdmin'

  const sensemakerDetail = useInstrumentTemplateDetail(templateId, enabled && !usePlatform)
  const platformDetail = usePlatformInstrumentTemplateDetail(templateId, enabled && usePlatform)

  const { data: detail, isLoading, isError } = usePlatform ? platformDetail : sensemakerDetail

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const previewRows = useMemo(() => {
    if (!detail?.questions?.length) return []
    const sortedSeeds = [...detail.questions].sort((a, b) => a.order - b.order)
    const draftRows = mapTemplateSeedToInstrumentDraft(sortedSeeds)
    const builderQs = mapInstrumentDraftToBuilderQuestions(draftRows)
    return builderQs.map((builderQuestion, index) => ({
      builderQuestion,
      apiType: sortedSeeds[index]?.type ?? 'multiple_choice',
      prompt: sortedSeeds[index]?.prompt ?? builderQuestion.questionText,
    }))
  }, [detail?.questions])

  const categoryLabel =
    categoryLabelProp ||
    (template === null ? '' : getTemplateCategoryLabel(template.category))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      data-testid="instrument-template-preview-modal"
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: '100%',
          maxWidth: 640,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '0.5px solid #e4e9f0',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          m: 2,
          overflow: 'hidden',
        },
      }}
      aria-labelledby="template-preview-title"
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${HEADER_BORDER}`,
          px: 3.5,
          pt: 3,
          pb: 2,
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 2 }}>
          {template ? <TemplateIcon iconKey={template.iconKey} size={44} /> : null}
          <Typography
            id="template-preview-title"
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '30px',
              color: TITLE,
            }}
          >
            {template?.name ?? 'Template preview'}
          </Typography>
        </Stack>

        {template ? (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                bgcolor: '#f4f7fa',
                border: '1px solid #e4e9f0',
              }}
            >
              <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#64748b' }}>
                {template.questionCount} questions
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                bgcolor: '#e6f4fb',
                border: '1px solid #c7ebf9',
              }}
            >
              <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#008cd0' }}>
                {formatTemplateEstCompletionLabel(
                  detail?.estCompletionSecs ?? template.estCompletionSecs,
                )}
              </Typography>
            </Box>
            {categoryLabel ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.875,
                  borderRadius: 999,
                  bgcolor: '#daf5e4',
                  border: '1.5px solid #bfefd1',
                }}
              >
                <Typography sx={{ fontSize: '12px', lineHeight: '18px', fontWeight: 500, color: '#0d9467' }}>
                  {categoryLabel}
                </Typography>
              </Box>
            ) : null}
            {showUsageCount && template ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.875,
                  borderRadius: 999,
                  bgcolor: '#f4f7fa',
                  border: '1px solid #e4e9f0',
                }}
              >
                <Typography
                  data-testid="instrument-template-preview-usage-count"
                  sx={{ fontSize: '14px', lineHeight: '20px', color: '#92400e' }}
                >
                  Used {template.publishedUsageCount ?? 0} times
                </Typography>
              </Box>
            ) : null}
          </Stack>
        ) : null}

        {template ? (
          <Typography sx={{ fontSize: '13px', lineHeight: '21px', color: DESC_BLUE }}>
            {template.shortDescription}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#ffffff',
          px: 3.5,
          pt: 2.5,
          pb: 2,
        }}
        data-testid="instrument-template-preview-questions"
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '15px',
            lineHeight: '22.5px',
            color: TITLE,
            mb: 2,
          }}
        >
          Template Questions
        </Typography>

        {isLoading ? <TemplatePreviewQuestionsSkeleton /> : null}

        {isError ? (
          <Typography sx={{ py: 2, color: '#b91c1c', fontSize: '14px' }}>
            Could not load template questions. Try again.
          </Typography>
        ) : null}

        {!isLoading && !isError ? (
          <Stack spacing={1.5}>
            {previewRows.map((row, index) => (
              <TemplatePreviewQuestionCard
                key={row.builderQuestion.id}
                index={index}
                apiType={row.apiType}
                prompt={row.prompt}
                builderQuestion={row.builderQuestion}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#ffffff',
          borderTop: `1px solid ${FOOTER_BORDER}`,
          px: 3.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          data-testid="instrument-template-preview-close"
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 500,
            borderColor: '#cbd5e1',
            color: '#475569',
            '&:hover': {
              borderColor: '#94a3b8',
              bgcolor: '#f8fafc',
            },
          }}
        >
          Close
        </Button>
        {previewOnly ? null : (
          <Button
            variant="contained"
            disabled={!template}
            onClick={() => {
              if (template && onUseTemplate) onUseTemplate(template)
            }}
            data-testid="instrument-template-preview-use"
            sx={libraryModalCloseButtonSx}
          >
            Use this template
          </Button>
        )}
      </Box>
    </Dialog>
  )
}
